import { HttpProgressEvent } from '@angular/common/http';
import { Injectable } from '@angular/core';

import { BehaviorSubject, Subject, Subscription } from 'rxjs';

import { BaseLogging } from '@base/base-logging';
import { Guid, IVzFileTpl, RecursivePartial, SM, Tag, Upload, UploadStatus, VzFile, getUuid } from '@models';
import { HttpApiError } from '@models/api/http-api-error';
import { ApiService } from './api.service';

@Injectable()
@Tag('UploadService')
export class UploadService extends BaseLogging {

    readonly uploads: BehaviorSubject<Upload[]> = new BehaviorSubject<Upload[]>([]);
    readonly created: Subject<Upload> = new Subject();
    readonly started: Subject<Upload> = new Subject();
    readonly progress: Subject<Upload> = new Subject();
    readonly finished: Subject<Upload> = new Subject();
    readonly removed: Subject<Upload> = new Subject();
    readonly previewReady: Subject<string> = new Subject();

    private _subs: SM<Subscription> = {};
    private _uploads: SM<Upload> = {};

    constructor(private _api: ApiService) {
        super();
    }

    uploadFile(fileTpl: IVzFileTpl, file: File, sourceId: string, sourceTitle: string, sourceLink?: string): Upload {
        const up = new Upload({ id: getUuid(), file, info: new VzFile(fileTpl), bytesTotal: file.size, sourceId, sourceTitle, sourceLink });
        this._uploads[up.id!] = up;
        this.created.next(up);
        this.uploads.next(Object.values(this._uploads));
        this._api.createFile(fileTpl).subscribe({
            next: fileId => {
                up.info!.id = fileId;
                this._startUpload(up);
            },
            error: err => {
                up.status = UploadStatus.Error;
                up.error = err;
                up.finished = new Date().getTime();
                this.finished.next(up);
                this._updateUpload(up);
            }
        });
        return up;
    }

    addUpload(upload: RecursivePartial<Upload>): Upload {
        const up = new Upload(upload);
        if (!up.id) {
            up.id = getUuid();
        }
        this._uploads[up.id] = up;
        this.created.next(up);
        this.uploads.next(Object.values(this._uploads));
        if (up.status == UploadStatus.Error && up.error) {
            this.finished.next(up);
            this._updateUpload(up);
        }
        else {
            this._startUpload(up);
        }
        return up;
    }


    removeUploadByFileId(fileId: string): void {
        const upload = Object.values(this._uploads).find(up => up.info?.id == fileId);
        if (upload?.id) {
            this.removeUpload(upload.id);
        }
    }


    removeUpload(id: string): void {
        const up = this._uploads[id];
        if (up) {
            if (this._subs[id]) {
                this._subs[id].unsubscribe();
            }
            delete this._subs[id];
            delete this._uploads[id];
            this.removed.next(up);
            this.uploads.next(Object.values(this._uploads));
        }
    }

    cancelUpload(id: string, reason: HttpApiError): void {
        const up = this._uploads[id];
        if (up) {
            if (this._subs[id]) {
                this._subs[id].unsubscribe();
            }
            delete this._subs[id];
            up.status = UploadStatus.Error;
            up.error = reason;
            up.finished = new Date().getTime();
            this.finished.next(up);
            this._updateUpload(up);
        }
    }

    getUploadByFileId(fileId?: Guid): Upload | undefined {
        return fileId ? Object.values(this._uploads).find(up => up.info && up.info.id == fileId) : undefined;
    }

    // private _startChunkUpload(up: Upload): void {
    //     if (!up || !up.info || !up.info.id || !up.file) {
    //         return;
    //     }
    //     const { readable, writable } = new TransformStream();
    //     let stop = false;
    //     this._api.uploadFileStream(up.info.id, readable).subscribe({
    //         next: res => this._L('_startChunksUpload', 'res:', res),
    //         error: err => {
    //             this._W('_startChunksUpload', 'error:', err);
    //             this._error.set(err);
    //             stop = true;
    //         }
    //     });
    //     const writer = writable.getWriter();
    //     this._L('_startChunksUpload', 'got streams \n\t readable:', readable, '\n\t writable:', writable, '\n\t writer:', writer);

    //     const CHUNK_SIZE = 2 * 1024;
    //     this._L('_startChunksUpload', 'readFileInChunks:', CHUNK_SIZE);
    //     let offset = 0;
    //     const file = up.file;
    //     const readNextChunk = () => {
    //         const blob = file.slice(offset, offset + CHUNK_SIZE);
    //         const reader = new FileReader();
    //         reader.onload = (e) => {
    //             if (stop) {
    //                 up.status = UploadStatus.Error;
    //                 up.error ||= new HttpApiError('sending chunk error');
    //                 up.finished = new Date().getTime();
    //                 this.finished.next(up);
    //                 this._updateUpload(up);
    //                 return;
    //             }
    //             this._L('_startChunksUpload', 'Chunk:', e.target);
    //             if (e.target?.result) {
    //                 writer.write(e.target.result);
    //                 offset += CHUNK_SIZE;
    //                 const finished = offset >= file.size;
    //                 const upBytes = finished ? up.bytesTotal : (e ? (e.loaded > up.bytesTotal ? up.bytesTotal : e.loaded) : 0);
    //                 up.finished = finished ? new Date().getTime() : undefined;
    //                 up.status = finished ? UploadStatus.Uploaded : UploadStatus.UploadStarted;
    //                 up.bytesUploaded = upBytes;
    //                 up.speed = this._getSpeed(up);
    //                 if (finished) {
    //                     this.finished.next(up);
    //                 }
    //                 this._updateUpload(up);
    //                 if (!finished) {
    //                     readNextChunk();
    //                 }
    //                 else {
    //                     this._L('_startChunksUpload', 'end of file chunks');
    //                     writer.close();
    //                     stop = true;
    //                 }
    //             }
    //             else {
    //                 this._L('_startChunksUpload', 'error receiving chunk');
    //                 writer.close();
    //                 up.status = UploadStatus.Error;
    //                 up.error = new HttpApiError('error receiving chunk');
    //                 up.finished = new Date().getTime();
    //                 this.finished.next(up);
    //                 this._updateUpload(up);
    //                 stop = true;
    //             }
    //         };
    //         reader.onerror = (e) => {
    //             this._L('_startChunksUpload', 'error reading file chunk:', e);
    //             writer.close();
    //             up.status = UploadStatus.Error;
    //             up.error = new HttpApiError('error receiving chunk2');
    //             up.finished = new Date().getTime();
    //             this.finished.next(up);
    //             this._updateUpload(up);
    //             stop = true;
    //         };
    //         reader.readAsArrayBuffer(blob);
    //     };
    //     readNextChunk();
    // }

    private _startUpload(up: Upload): void {
        up.started = new Date().getTime();
        up.status = UploadStatus.UploadStarted;
        this.started.next(up);
        this._updateUpload(up);
        this._subs[up.id!] = this._api.uploadFile(up.info!.id!, up.file!, {
            requestId: rid => this._uploadStarted(rid, up),
            upload: e => this._uploadProgress(up, e)
        }).subscribe({
            next: () => this._uploadProgress(up, undefined, true),
            error: err => {
                up.status = UploadStatus.Error;
                up.error = err;
                up.finished = new Date().getTime();
                this.finished.next(up);
                this._updateUpload(up);
            },
        });
    }

    private _uploadStarted(reqId: string, up: Upload): void {
        up.started = new Date().getTime();
        up.requestId = reqId;
        up.status = UploadStatus.UploadStarted;
        this._updateUpload(up);
    }

    private _uploadProgress(up: Upload, e?: HttpProgressEvent, finished = false): void {
        const upBytes = finished ? up.bytesTotal : (e ? (e.loaded > up.bytesTotal ? up.bytesTotal : e.loaded) : 0);
        up.finished = finished ? new Date().getTime() : undefined;
        up.status = finished ? UploadStatus.Uploaded : UploadStatus.UploadStarted;
        up.bytesUploaded = upBytes;
        up.speed = this._getSpeed(up);
        if (finished) {
            this.finished.next(up);
        }
        this._updateUpload(up);
    }

    private _getSpeed(up: Upload): number {
        if (!up || !up.started || (up.status != UploadStatus.UploadStarted && up.status != UploadStatus.Uploaded)) {
            return 0;
        }
        const time = (up.status == UploadStatus.Uploaded && up.finished ? up.finished : new Date().getTime()) - up.started;
        return up.bytesUploaded / time * 1000;
    }

    private _updateUpload(upload: Upload): void {
        this.progress.next(upload);
        this.uploads.next(Object.values(this._uploads));
    }

}
